"use client"

import { Slot } from "@radix-ui/react-slot"
import React, { useEffect, useRef, useState } from "react"
import { useIsMobile } from "../../hooks/use-mobile"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../shadcn/hover-card"

/**
 * @description 호버로 열리는 메뉴. TMenu와 동일한 API지만 hover 트리거. 모바일에서는 클릭으로 토글
 * @example
 * <THoverMenu
 *   renderButton={({ open }) => <TButton>메뉴</TButton>}
 *   renderMenu={({ onClose }) => (
 *     <div>
 *       <TMenuItem label="항목 1" onClick={onClose} />
 *     </div>
 *   )}
 * />
 */
interface THoverMenuProps {
	/** 메뉴 트리거 버튼 렌더 함수 (open 상태 전달) */
	renderButton: (props: { open?: boolean }) => React.ReactNode
	/** 메뉴 콘텐츠 렌더 함수 (onClose 콜백 전달) */
	renderMenu: (props: { onClose: () => void }) => React.ReactNode
	/** 외부 제어 열림 상태 */
	open?: boolean
	/**
	 * 메뉴 정렬
	 * @default "start"
	 */
	align?: "start" | "center" | "end"
	/**
	 * 메뉴 표시 방향
	 * @default "bottom"
	 */
	side?: "top" | "right" | "bottom" | "left"
	/** 정렬 오프셋 (px) */
	alignOffset?: number
	/** 방향 오프셋 (px) */
	sideOffset?: number
	/** 추가 CSS 클래스 */
	className?: string
	/** 메뉴 닫힘 콜백 */
	onMenuClose?: () => void
	/** 메뉴 열림 콜백 */
	onMenuOpen?: () => void
}

export const THoverMenu = React.memo((props: THoverMenuProps) => {
	const { renderButton, renderMenu, open, align = "start", side = "bottom", alignOffset = 0, sideOffset = 4, className, onMenuClose, onMenuOpen } = props

	const isMobile = useIsMobile()
	const [isOpen, setIsOpen] = useState(false)
	const [isHoverOpen, setIsHoverOpen] = useState(false)
	const [isClickLocked, setIsClickLocked] = useState(false)
	const isClickingRef = useRef(false)
	const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

	// unmount 시 closeTimeoutRef cleanup — 미정리 시 unmount 후 setIsOpen 호출로 React warning + 메모리 누수.
	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current)
				closeTimeoutRef.current = null
			}
		}
	}, [])

	const handleOpen = React.useCallback(() => {
		setIsOpen(true)
		onMenuOpen?.()
	}, [onMenuOpen])

	const handleClose = React.useCallback(() => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current)
			closeTimeoutRef.current = null
		}
		setIsOpen(false)
		setIsHoverOpen(false)
		setIsClickLocked(false)
		onMenuClose?.()
	}, [onMenuClose])

	const handleTriggerClick = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			e.stopPropagation()

			// 모바일에서는 클릭으로 토글
			if (isMobile) {
				if (isOpen) {
					handleClose()
				} else {
					handleOpen()
				}
				return
			}

			if (isHoverOpen || isOpen) {
				setIsClickLocked(true)
				setIsHoverOpen(true)

				if (!isOpen) {
					handleOpen()
				}

				setTimeout(() => {
					isClickingRef.current = false
				}, 200)
				return
			}

			setIsHoverOpen(false)
			setIsClickLocked(false)
			handleOpen()

			setTimeout(() => {
				isClickingRef.current = false
			}, 200)
		},
		[isMobile, isHoverOpen, isOpen, handleOpen, handleClose]
	)

	const onOpenChange = React.useCallback(
		(open: boolean) => {
			// 모바일에서는 hover 이벤트 무시 (클릭으로만 제어)
			if (isMobile) return

			if (isClickingRef.current) {
				return
			}

			if (isClickLocked && !open) {
				setIsClickLocked(false)
			}

			if (open) {
				if (closeTimeoutRef.current) {
					clearTimeout(closeTimeoutRef.current)
					closeTimeoutRef.current = null
				}
				setIsHoverOpen(true)
				handleOpen()
			} else {
				closeTimeoutRef.current = setTimeout(() => {
					handleClose()
					closeTimeoutRef.current = null
				}, 100)
			}
		},
		[isMobile, isClickLocked, handleOpen, handleClose]
	)

	const handleMouseDown = React.useCallback(() => {
		isClickingRef.current = true

		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current)
			closeTimeoutRef.current = null
		}
	}, [])

	return (
		<HoverCard open={open ?? isOpen} onOpenChange={onOpenChange} openDelay={100} closeDelay={100}>
			<HoverCardTrigger asChild>
				{/* Radix Slot 직접 사용 — wrap element (div/span) 추가하지 않고 자식 (renderButton 결과 button) 자체가 trigger.
				    이전 div/span wrap 은 React 19 SSR 환경에서 hydration mismatch 회귀 (server: button trigger, client: wrap trigger).
				    Slot 은 자식 props 에 onClick/onMouseDown merge — server/client 모두 button trigger 로 동일. */}
				<Slot onClick={handleTriggerClick} onMouseDown={handleMouseDown}>
					{renderButton({ open: open ?? isOpen })}
				</Slot>
			</HoverCardTrigger>
			<HoverCardContent align={align} side={side} alignOffset={alignOffset} sideOffset={sideOffset} className={className}>
				{renderMenu({ onClose: handleClose })}
			</HoverCardContent>
		</HoverCard>
	)
})

THoverMenu.displayName = "THoverMenu"
